import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SortDirection, TransactionTypesCriteria } from '@e-origin/shared';

import { showErrorToast } from '@hooks/showErrorToast';
import { apiClient } from '@utils';

export const defaultTransactionTypesCriteria: TransactionTypesCriteria = {
  filters: { searchQuery: '' },
  pagination: { page: 1, size: 10 },
  sorting: { field: 'name', direction: SortDirection.ASC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): TransactionTypesCriteria => {
  const filters: TransactionTypesCriteria['filters'] = {};
  filters.searchQuery = searchParams.get('searchQuery') || '';

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'name';
  const sortDir = (searchParams.get('sortDir') || SortDirection.ASC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: TransactionTypesCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialTransactionTypeCriteria = (searchParams: URLSearchParams): TransactionTypesCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultTransactionTypesCriteria;
};

export const useTransactionTypeCriteriaUrlSync = (criteria?: TransactionTypesCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

export const useTransactionTypes = (criteria: TransactionTypesCriteria = defaultTransactionTypesCriteria) => {
  return useQuery({
    queryKey: ['transaction-types', criteria],
    queryFn: async () => {
      try {
        const result = await apiClient.transactionType.retrieveTransactionTypes.mutation({ body: { criteria } });
        return (result.body as any).data;
      } catch (error) {
        showErrorToast(error, 'Failed to fetch transaction types list');
        return { data: { transactionTypes: [], totalItems: 0 } };
      }
    },
  });
};
